/**
 * Billing hooks
 * =============
 *
 * React Query hooks for the embedded-Elements billing surface. Billing
 * is org-scoped on the backend; the active org is resolved from
 * ``User.current_organization_id`` on each request - no org id is passed
 * from the client.
 *
 * The previous Checkout-redirect helpers are gone - purchases and plan
 * changes are now fully inline using ``@stripe/react-stripe-js``.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  billingApi,
  type MySubscriptionResponse,
  type SubscriptionDetail,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

// ── Reads ─────────────────────────────────────────────────────────────

/** Catalog of plans. Rarely changes, but keep a modest stale window so
 *  catalog/copy edits surface within minutes (not an hour). */
export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billing.plans(),
    queryFn: () => billingApi.listPlans(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Active subscription for the user's current organization.
 *
 * ``pollingEnabled`` is used after a 3DS challenge / payment confirmation
 * so the UI catches the webhook within a few seconds rather than waiting
 * for the user to refresh.
 */
export function useSubscription(options?: {
  pollingEnabled?: boolean;
  enabled?: boolean;
}) {
  return useQuery<MySubscriptionResponse>({
    queryKey: queryKeys.billing.subscription(),
    queryFn: () => billingApi.getMySubscription(),
    staleTime: 30 * 1000,
    refetchInterval: options?.pollingEnabled ? 3000 : false,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Current AI-credit balance + trial state for the meter and trial UI.
 * Poll after a generation so the meter reflects spend within a few seconds.
 */
export function useUsage(options?: { pollingEnabled?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.billing.all, 'usage'] as const,
    queryFn: () => billingApi.getUsage(),
    staleTime: 30 * 1000,
    refetchInterval: options?.pollingEnabled ? 5000 : false,
  });
}

/**
 * Per-feature daily/monthly usage (lessons, chat, practice, podcast, ...) for
 * the limit modal and any usage UI. Distinct from ``useUsage``, which reads the
 * internal dollar/credit backstop (not shown under the daily-limit model).
 */
export function useEntitlementsUsage(options?: { pollingEnabled?: boolean }) {
  return useQuery({
    queryKey: [...queryKeys.billing.all, 'entitlements-usage'] as const,
    queryFn: () => billingApi.getEntitlementsUsage(),
    staleTime: 30 * 1000,
    refetchInterval: options?.pollingEnabled ? 5000 : false,
  });
}

/** Start the one-time, no-card free trial; refreshes usage + subscription. */
export function useStartTrial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.startTrial(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    },
  });
}

export function useInvoices(limit = 10) {
  return useQuery({
    queryKey: queryKeys.billing.invoices(limit),
    queryFn: () => billingApi.listInvoices(limit),
    staleTime: 60 * 1000,
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: [...queryKeys.billing.all, 'payment-methods'] as const,
    queryFn: () => billingApi.listPaymentMethods(),
    staleTime: 60 * 1000,
  });
}

export function useProrationPreview(newPlanId: string | null) {
  return useQuery({
    queryKey: [
      ...queryKeys.billing.all,
      'proration-preview',
      newPlanId,
    ] as const,
    // Per-request 10s timeout via AbortController. Without this, a stuck
    // Stripe call would let the modal's spinner spin for ~30s before
    // React Query's default fetch timeout kicks in. The backend now has
    // a Redis cache + a non-blocking event loop, so a 10s ceiling is
    // generous for the cold path and effectively never trips on a warm
    // cache hit.
    queryFn: ({ signal: rqSignal }) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10_000);
      // Forward React Query's cancel signal (e.g. dialog close) so the
      // in-flight request is dropped immediately.
      if (rqSignal) {
        if (rqSignal.aborted) controller.abort();
        else rqSignal.addEventListener('abort', () => controller.abort());
      }
      return billingApi
        .prorationPreview(newPlanId!, { signal: controller.signal })
        .finally(() => clearTimeout(timer));
    },
    enabled: !!newPlanId,
    staleTime: 30 * 1000,
    // Single retry with a flat 500ms delay. The default 3 retries with
    // exponential backoff (1s → 2s → 4s) compounded with the 10s
    // per-request ceiling could otherwise burn ~17s before erroring.
    retry: 1,
    retryDelay: 500,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────

/** Mint a SetupIntent - used by ``AddPaymentMethodForm`` to render the
 *  PaymentElement. */
export function useCreateSetupIntent() {
  return useMutation({
    mutationFn: () => billingApi.createSetupIntent(),
  });
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      billingApi.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.billing.all, 'payment-methods'],
      });
    },
  });
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      billingApi.deletePaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.billing.all, 'payment-methods'],
      });
    },
  });
}

/** Create the subscription. Returns ``client_secret`` for 3DS confirmation. */
export function useCreateSubscription() {
  return useMutation({
    mutationFn: (input: { plan_id: string; checkout_id: string }) =>
      billingApi.createSubscription(input),
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newPlanId: string) => billingApi.changePlan(newPlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    },
  });
}

/**
 * Prime the subscription cache from a mutation's returned `SubscriptionDetail`
 * so cancel/resume/undo reflect immediately (authoritative server value),
 * instead of waiting on a refetch that can briefly return stale state. The
 * cache holds `MySubscriptionResponse`, so we merge the detail into its
 * `.subscription`. Then invalidate to revalidate tier/plan/etc.
 */
function useSubscriptionMutation(
  mutationFn: () => Promise<SubscriptionDetail>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async updated => {
      // Cancel any in-flight subscription refetch first: a trailing GET from a
      // prior action's invalidation could otherwise resolve LATER and clobber
      // this authoritative value (the cancel→resume race). The mutation
      // response is the source of truth, so we write it directly and don't
      // re-invalidate the subscription query.
      await queryClient.cancelQueries({
        queryKey: queryKeys.billing.subscription(),
      });
      queryClient.setQueryData<MySubscriptionResponse>(
        queryKeys.billing.subscription(),
        prev => (prev ? { ...prev, subscription: updated } : prev)
      );
      // Refresh sibling billing data (invoices/usage) WITHOUT refetching the
      // subscription query we just authoritatively set.
      queryClient.invalidateQueries({
        queryKey: queryKeys.billing.all,
        refetchType: 'none',
      });
    },
  });
}

export function useCancelSubscription() {
  return useSubscriptionMutation(() => billingApi.cancelSubscription());
}

export function useResumeSubscription() {
  return useSubscriptionMutation(() => billingApi.resumeSubscription());
}

/** Undo a pending at-period-end plan change (annual → monthly). */
export function useUndoScheduledChange() {
  return useSubscriptionMutation(() => billingApi.undoScheduledChange());
}

/**
 * Force-sync the subscription from Stripe, then prime the cache with the
 * result. Call after checkout / plan change so Settings reflects the final
 * state immediately, independent of webhook delivery/timing.
 */
export function useRefreshSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.refreshSubscription(),
    onSuccess: data => {
      queryClient.setQueryData(queryKeys.billing.subscription(), data);
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
    },
  });
}

/** Convenience: blow away all billing-related cache. */
export function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
}
